import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { RootState } from '../../store/store';
import { employeeApi } from '../../api/employeeApi';
import { administrationApi } from '../../api/administrationApi';
import { masterApi } from '../../api/masterApi';
import { getUploadsBaseUrl } from '../../config/runtimeConfig';
import { DropdownItem, EmployeeDirectoryItem } from '../../types';

interface OrganizationOption {
  id: string;
  name: string;
}

const filterOptions = [
  { value: 'branch', label: 'Branch' },
  { value: 'location', label: 'Location' },
  { value: 'department', label: 'Department' },
  { value: 'designation', label: 'Designation' },
];

const getPhotoUrl = (filePath?: string | null) => {
  if (!filePath) return undefined;
  if (/^https?:\/\//i.test(filePath)) return filePath;
  return `${getUploadsBaseUrl()}/${filePath.replace(/^\/+/, '')}`;
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const normalizeRoleView = (roles: string[] = []) => {
  const normalized = roles.map((role) => role.toLowerCase().replace(/\s+/g, ''));

  if (normalized.some((role) => role.includes('admin'))) {
    return 'Admin';
  }

  if (normalized.some((role) => role.includes('teamlead') || role.includes('reportingmanager'))) {
    return 'Team Lead';
  }

  return 'Employee';
};

const EmployeeDirectory: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [rows, setRows] = useState<EmployeeDirectoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [organizationId, setOrganizationId] = useState(user?.organizationId || '');
  const [branches, setBranches] = useState<DropdownItem[]>([]);
  const [departments, setDepartments] = useState<DropdownItem[]>([]);
  const [designations, setDesignations] = useState<DropdownItem[]>([]);
  const [filterBy, setFilterBy] = useState('branch');
  const [filterValue, setFilterValue] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const roleView = useMemo(() => normalizeRoleView(user?.roles), [user?.roles]);

  useEffect(() => {
    let active = true;

    const loadFilters = async () => {
      try {
        const requests: Promise<any>[] = [
          masterApi.getBranches(),
          masterApi.getDepartments(),
          masterApi.getDesignations(),
        ];

        if (user?.tenantId) {
          requests.push(administrationApi.getOrganizations(user.tenantId));
        }

        const [branchItems, departmentItems, designationItems, organizationItems] = await Promise.all(requests);
        if (!active) return;

        setBranches(branchItems || []);
        setDepartments(departmentItems || []);
        setDesignations(designationItems || []);
        setOrganizations(
          (organizationItems || []).map((organization: any) => ({
            id: organization.id,
            name: organization.organizationFullName || organization.organizationShortName,
          }))
        );
      } catch {
        if (!active) return;
        setBranches([]);
        setDepartments([]);
        setDesignations([]);
        setOrganizations([]);
      }
    };

    loadFilters();

    return () => {
      active = false;
    };
  }, [user?.tenantId]);

  useEffect(() => {
    let active = true;

    const loadDirectory = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await employeeApi.getDirectory({
          organizationId: organizationId || undefined,
          branchId: filterBy === 'branch' && filterValue ? filterValue : undefined,
          departmentId: filterBy === 'department' && filterValue ? filterValue : undefined,
          designationId: filterBy === 'designation' && filterValue ? filterValue : undefined,
          location: filterBy === 'location' && filterValue ? filterValue : undefined,
        });

        if (!active) return;
        setRows(data);
      } catch (err: any) {
        if (!active) return;
        setError(err?.response?.data?.message || 'Failed to load employee directory');
        setRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDirectory();

    return () => {
      active = false;
    };
  }, [filterBy, filterValue, organizationId]);

  useEffect(() => {
    setSelectedIds((previous) => previous.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  useEffect(() => {
    if (selectedEmployeeId !== 'all' && !rows.some((row) => row.id === selectedEmployeeId)) {
      setSelectedEmployeeId('all');
    }
  }, [rows, selectedEmployeeId]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const organizationMatch =
        !organizationId || !row.organizationId || row.organizationId === organizationId;

      if (!organizationMatch) {
        return false;
      }

      if (selectedEmployeeId !== 'all' && row.id !== selectedEmployeeId) {
        return false;
      }

      return true;
    });
  }, [organizationId, rows, selectedEmployeeId]);

  const locationOptions = useMemo(
    () =>
      Array.from(
        new Map(
          rows
            .filter((row) => row.workLocation)
            .map((row) => [row.workLocation as string, row.workLocation as string])
        ).entries()
      ).map(([id, name]) => ({ id, name })),
    [rows]
  );

  const employeeOptions = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        name: row.fullName,
      })),
    [rows]
  );

  const currentFilterOptions = useMemo(() => {
    switch (filterBy) {
      case 'branch':
        return branches;
      case 'department':
        return departments;
      case 'designation':
        return designations;
      case 'location':
        return locationOptions;
      default:
        return [];
    }
  }, [branches, departments, designations, filterBy, locationOptions]);

  const handleFilterByChange = (event: SelectChangeEvent<string>) => {
    setFilterBy(event.target.value);
    setFilterValue('');
  };

  const allSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedIds.includes(row.id));
  const someSelected = filteredRows.some((row) => selectedIds.includes(row.id));

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds((previous) => Array.from(new Set([...previous, ...filteredRows.map((row) => row.id)])));
      return;
    }

    const visibleIdSet = new Set(filteredRows.map((row) => row.id));
    setSelectedIds((previous) => previous.filter((id) => !visibleIdSet.has(id)));
  };

  const handleToggleRow = (id: string) => {
    setSelectedIds((previous) =>
      previous.includes(id) ? previous.filter((item) => item !== id) : [...previous, id]
    );
  };

  const handleExport = () => {
    const exportRows = selectedIds.length > 0
      ? filteredRows.filter((row) => selectedIds.includes(row.id))
      : filteredRows;

    if (exportRows.length === 0) {
      toast.error('No employee data available to export');
      return;
    }

    const csvRows = [
      ['Employee Code', 'Name', 'Mobile Number', 'Email'],
      ...exportRows.map((row) => [
        row.employeeCode,
        row.fullName,
        row.mobileNumber || '',
        row.email || row.officialEmail || row.personalEmail || '',
      ]),
    ];

    const csvContent = csvRows
      .map((columns) => columns.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `employee-directory-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="text"
            startIcon={<BackIcon />}
            onClick={() => navigate('/employees')}
            sx={{ minWidth: 'auto' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Employee Directory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {roleView.toLowerCase()} scoped records with role-based visibility.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Total Records: {filteredRows.length}
          </Typography>
          <Chip label={`Role: ${roleView}`} color="primary" variant="outlined" />
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || filteredRows.length === 0}
              sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>Organization</InputLabel>
            <Select
              value={organizationId}
              label="Organization"
              onChange={(event) => setOrganizationId(event.target.value)}
              disabled={roleView !== 'Admin'}
            >
              <MenuItem value="">All</MenuItem>
              {organizations.map((organization) => (
                <MenuItem key={organization.id} value={organization.id}>
                  {organization.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Filter By</InputLabel>
            <Select
              value={filterBy}
              label="Filter By"
              onChange={handleFilterByChange}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>{filterBy === 'location' ? 'Location' : 'Value'}</InputLabel>
            <Select
              value={filterValue}
              label={filterBy === 'location' ? 'Location' : 'Value'}
              onChange={(event) => setFilterValue(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {currentFilterOptions.map((option) => (
                <MenuItem key={String(option.id)} value={String(option.id)}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Select All</InputLabel>
            <Select
              value={selectedEmployeeId}
              label="Select All"
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {employeeOptions.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="info">No employee directory records found.</Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onChange={(event) => handleToggleSelectAll(event.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Photo</TableCell>
                  <TableCell>Employee Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Mobile Number</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  const email = row.email || row.officialEmail || row.personalEmail || '-';
                  const isSelected = selectedIds.includes(row.id);

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleToggleRow(row.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleRow(row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.fullName}>
                          <Avatar src={getPhotoUrl(row.profileImagePath)} sx={{ width: 36, height: 36 }}>
                            {getInitials(row.fullName)}
                          </Avatar>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.employeeCode}</TableCell>
                      <TableCell>{row.fullName}</TableCell>
                      <TableCell>{row.mobileNumber || '-'}</TableCell>
                      <TableCell>{email}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeDirectory;
